import React from 'react';
import { Box, Container, Typography, Grid, Avatar, Card, CardContent } from '@mui/material';
import { AiFillStar } from 'react-icons/ai';
import './Testimonial.css';

// Default testimonials (can be overridden via props)
import ProfilePic1 from '../../Assets/daniela.png';
import ProfilePic2 from '../../Assets/simone.png';
import ProfilePic3 from '../../Assets/bruno.png';

const defaultTestimonials = [
  {
    id: 1,
    name: 'Simone Silva',
    text: 'Hortaliças de excelente qualidade, mudas de hortaliças de ótima qualidade. Morango, cogumelos, tudo sem agrotóxicos. Vale a pena conferir.',
    rating: 5,
    img: ProfilePic1
  },
  {
    id: 2,
    name: 'Daniela Aparecida',
    text: 'Sou confeiteira e tenho sempre problemas para encontrar morangos de qualidade, que durem. Os morangos da Verde Musgo são incríveis, saborosos e duram muito mais que os convencionais.',
    rating: 5,
    img: ProfilePic2
  },
  {
    id: 3,
    name: 'Bruno Costa',
    text: 'Ótima iniciativa apoiar pequenos produtores — produtos saborosos e com muita qualidade. Atendimento carinhoso, preocupado. Entrega cuidadosa. Recomendo muito!',
    rating: 4,
    img: ProfilePic3
  }
];

const TestimonialCard = ({ testimonial, showRating = true }) => {
  return (
    <Card
      className="testimonial-card"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: 3,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-5px)'
        }
      }}
    >
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          p: 3,
          flex: 1
        }}
      >
        {/* Avatar */}
        <Avatar
          src={testimonial.img}
          alt={`${testimonial.name} - foto do perfil`}
          sx={{
            width: { xs: 80, md: 100 },
            height: { xs: 80, md: 100 },
            mb: 2,
            border: '4px solid #C5F0C2'
          }}
        />

        {/* Rating Stars */}
        {showRating && testimonial.rating && (
          <Box
            className="testimonials-stars-container"
            sx={{
              display: 'flex',
              gap: 0.5,
              mb: 2,
              color: '#FE9E0D'
            }}
          >
            {Array.from({ length: testimonial.rating }).map((_, i) => (
              <AiFillStar key={i} size={20} />
            ))}
          </Box>
        )}

        {/* Testimonial Text */}
        <Typography
          variant="body1"
          sx={{
            fontFamily: '"Reem Kufi", sans-serif',
            fontSize: { xs: '0.95rem', md: '1rem' },
            fontWeight: 400,
            color: '#505050',
            lineHeight: 1.7,
            mb: 2,
            flex: 1,
            fontStyle: 'italic'
          }}
        >
          "{testimonial.text}"
        </Typography>

        {/* Name */}
        <Typography
          variant="h3"
          sx={{
            fontFamily: '"Reem Kufi", sans-serif',
            fontSize: { xs: '1.1rem', md: '1.25rem' },
            fontWeight: 600,
            color: '#1B422E'
          }}
        >
          {testimonial.name}
        </Typography>

        {/* Position/Role (optional) */}
        {testimonial.role && (
          <Typography
            variant="body2"
            sx={{
              fontFamily: '"Reem Kufi", sans-serif',
              fontSize: '0.9rem',
              color: '#6a6a6a',
              mt: 0.5
            }}
          >
            {testimonial.role}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const Testimonial = ({
  testimonials = defaultTestimonials,
  title = 'O que dizem sobre nós',
  subtitle = 'Depoimentos',
  description = 'Veja o feedback de clientes que provaram nossos produtos e aprovaram a qualidade e o atendimento.',
  showRating = true,
  layout = 'grid', // 'grid' | 'carousel' (carousel pode ser implementado depois)
  backgroundColor = '#f9f9f9',
  maxWidth = 'lg'
}) => {
  return (
    <Box
      id="testimonial"
      className="testimonial-section"
      sx={{
        backgroundColor: backgroundColor,
        py: { xs: 5, md: 7 }
      }}
    >
      <Container maxWidth={maxWidth}>
        {/* Section Header */}
        <Box
          className="testimonial-header"
          sx={{
            textAlign: 'center',
            mb: { xs: 4, md: 5 }
          }}
        >
          {subtitle && (
            <Typography
              className="primary-subheading"
              sx={{
                fontFamily: '"Reem Kufi", sans-serif',
                fontSize: { xs: '1rem', md: '1.1rem' },
                fontWeight: 600,
                color: '#FE9E0D',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                mb: 1
              }}
            >
              {subtitle}
            </Typography>
          )}

          <Typography
            variant="h2"
            className="primary-heading"
            sx={{
              fontFamily: '"Reem Kufi", sans-serif',
              fontSize: { xs: 'clamp(1.75rem, 4vw, 2.5rem)', md: 'clamp(2rem, 4vw, 3rem)' },
              fontWeight: 700,
              color: '#1B422E',
              mb: 2,
              lineHeight: 1.3
            }}
          >
            {title}
          </Typography>

          {description && (
            <Typography
              className="primary-text"
              sx={{
                fontFamily: '"Reem Kufi", sans-serif',
                fontSize: { xs: '1rem', md: '1.1rem' },
                color: '#505050',
                maxWidth: '700px',
                mx: 'auto',
                lineHeight: 1.6
              }}
            >
              {description}
            </Typography>
          )}
        </Box>

        {/* Testimonials Grid */}
        <Grid container spacing={{ xs: 3, md: 4 }}>
          {testimonials.map((testimonial) => (
            <Grid item xs={12} sm={6} md={4} key={testimonial.id}>
              <TestimonialCard testimonial={testimonial} showRating={showRating} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Testimonial;
export { TestimonialCard };
